import { updateIndustryOrderApi } from "@/api/services/admin/learning-content";
import { TGetIndustryListItemDto } from "@/api/services/admin/learning-content/dtos/get-industry-list.dto";
import { IconPlus } from "@/assets/icons";
import { CardContainer } from "@/components/commons/CardContainer";
import { Button } from "@/components/elements/Button";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { LearningContentTable } from "@/features/learning-content/components/LearningContentTable";
import { EXAM_TYPE_LABELS } from "@/features/learning-content/constants/examOptions";
import { useIndustryList } from "@/features/learning-content/hooks/useIndustryList";
import { TLearningContentCategory } from "@/features/learning-content/types/learningContent";
import { useDraggableTable } from "@/hooks/useDraggableTable";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

type TLearningContentPageProps = {};

// Convert API response to frontend format
const mapIndustryToCategory = (item: TGetIndustryListItemDto & { isPublished?: boolean }): TLearningContentCategory => ({
  id: item.id,
  name: item.name,
  isPublished: item.isPublished ?? false,
  subjectCount: item.activeSubjectCount ?? 0,
  examType: EXAM_TYPE_LABELS[item.examType] ?? item.examType,
  nextExamDate: item.nextExamDate ?? "",
  internalNote: item.internalMemo ?? "",
  updatedAt: item.updatedAt ?? "",
  isRegistered: (item.activeSubjectCount ?? 0) > 0,
  weekPublishStatus: item.weekPublishStatus ?? null,
  publishedWeekCount: item.publishedWeekCount ?? 0,
});

export const LearningContentPage: FC<TLearningContentPageProps> = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: apiResponse, isLoading, isFetching } = useIndustryList();

  // Transform API response to match frontend types
  const tableData = useMemo<TLearningContentCategory[]>(() => {
    if (!apiResponse) return [];
    return apiResponse.data.map(mapIndustryToCategory);
  }, [apiResponse]);

  const totalCount = apiResponse?.pagination.total ?? 0;

  // Stable callback for getting item ID
  const getItemId = useCallback((item: TLearningContentCategory) => item.id, []);

  // Handle reorder with immediate API call (optimistic update with rollback on error)
  const handleReorder = useCallback(
    async (orderedIds: (string | number)[]) => {
      try {
        // Reverse IDs: backend assigns displayOrder by index and sorts DESC,
        // so first visual item needs the highest displayOrder
        await updateIndustryOrderApi({ industryIds: [...(orderedIds as number[])].reverse() });
        toast.success(API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_LIST] });
      } catch (error) {
        showError(error);
        throw error; // Re-throw to trigger rollback in hook
      }
    },
    [toast, queryClient],
  );

  // Draggable table hook - calls API immediately on drop
  const { rows, isSaving, onDragEnd } = useDraggableTable({
    initialData: tableData,
    getItemId,
    onReorder: handleReorder,
  });

  const handleAddIndustry = () => {
    navigate(AppRoutes.industryCreate);
  };

  return (
    <Stack sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        <TopPage title="教材と試験情報の管理 - 受験分野一覧" enablePageBack={false} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="body14" sx={{ color: "text.secondary" }}>
            現在登録されている分野および有効状態を示します。すべてのデータが揃っている場合、分野を有効化できます。
          </Typography>
        </Box>

        <CardContainer sx={{ borderRadius: "8px" }}>
          <Stack gap={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body14Semi">{totalCount} 件を表示</Typography>
              <Button size="sm" variant="primary" onClick={handleAddIndustry} startIcon={<IconPlus />}>
                分野を追加
              </Button>
            </Stack>
            <LearningContentTable isLoading={isLoading} isFetching={isFetching || isSaving} rows={rows} onDragEnd={onDragEnd} />
            <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
              コンテンツはWEEK別コンテンツ、単語帳、模擬試験があります。WEEK別コンテンツはカリキュラムに影響するため下書き状態で編集し、最後に公開処理する必要があります。
              <br />
              対して、単語帳と模擬試験は編集すると即時反映されます。コンテンツ無しで分野の提供を開始・有効化するとお客様は手続き管理のみ利用でき、学習アプリは利用できません。
            </Typography>
          </Stack>
        </CardContainer>
      </Box>
    </Stack>
  );
};
